import { getCurrentInstance } from 'vue';
import { useRouter, useStore } from '@/utils/vue-api';
import { useCommonData } from '@/hooks';
import { useNavigation } from '@rebuild/hooks/useNavigation';
import { enterprise } from '@/axios/interface';
import instance from '@/axios';
import to from 'await-to-js';
import i18n, { getLanguageByType } from '@/locales';
import { resolveStoreId } from '@rebuild/utils/context';
import { buildModuleMenuMap } from '@/store/buildMenuIndex';
import LoadingMask from '@rebuild/components/loading/loading';

// 自定义模块“招股书”
const PROSPECTUS_MODULE_ID = '76';

// 速腾聚创菜单页切语时，仅这些模块保持当前内容，其余场景继续回退首页
const PRESERVE_MODULE_IDS = new Set([
	'announcement',
	PROSPECTUS_MODULE_ID,
	'investment_activity',
	'quote',
	'performance_report',
	'corporate_governance'
]);

// 普通访问场景下按页面缓存菜单映射，避免重复请求
const pageMenuMapCache = new Map();

const normalizeModuleId = moduleId => {
	if (moduleId === undefined || moduleId === null || moduleId === '') return '';
	return String(moduleId);
};

export function usePreserveMenuLanguageSwitch() {
	const vm = getCurrentInstance();
	const store = useStore();
	const router = useRouter();
	const { viewInIframe, viewInPreview } = useCommonData();
	const { buildTemplatePath, buildTemplateQuery, buildMenuPath } = useNavigation();

	const getTargetPage = language => {
		return (store.getters.getPageList || []).find(page => {
			return page.languageSwitch && getLanguageByType(page.languageType) === language;
		});
	};

	const getPageModuleMenuMap = async page => {
		const storeId = resolveStoreId();
		if (!storeId || !page?.pageId) return null;

		const usePreviewApi = viewInPreview.value;
		const cacheKey = `${storeId}:${page.pageId}`;
		if (!usePreviewApi && pageMenuMapCache.has(cacheKey)) {
			return pageMenuMapCache.get(cacheKey);
		}

		const promise = (async () => {
			const api = usePreviewApi
				? enterprise.getPageSaveContent(storeId, page.pageId)
				: enterprise.getPageDetail(storeId, page.pageId);
			const [err, res] = await to(instance.get(api));
			if (err) return null;

			const data = res?.data?.data || {};
			return buildModuleMenuMap(data.pageMenuVOS || []);
		})();

		if (!usePreviewApi) {
			pageMenuMapCache.set(cacheKey, promise);
		}

		let result;
		try {
			result = await promise;
		} catch (error) {
			if (!usePreviewApi) pageMenuMapCache.delete(cacheKey);
			throw error;
		}

		if (!result && !usePreviewApi) {
			pageMenuMapCache.delete(cacheKey);
		}
		return result;
	};

	const getCurrentModuleId = () => {
		const route = vm.proxy.$route;
		const routeModuleId = normalizeModuleId(route.params?.moduleId);

		if (routeModuleId) return routeModuleId;

		const menuId = route.params?.menuId;
		const currentMenu = (store.getters.getNormalMenus || []).find(menu => String(menu.id) === String(menuId));
		return normalizeModuleId(currentMenu?.moduleVOS?.[0]?.moduleId);
	};

	const handleLanguageSelect = async language => {
		// iframe 预览保持基准行为：展示语言按钮但不切换
		if (viewInIframe.value) return true;
		if (!language || language === i18n.locale) return true;

		const route = vm.proxy.$route;
		if (route.meta?.source !== 'menu' || !route.params?.menuId) return false;

		const moduleId = getCurrentModuleId();
		if (!moduleId || !PRESERVE_MODULE_IDS.has(moduleId)) return false;

		const targetPage = getTargetPage(language);
		if (!targetPage) return false;

		LoadingMask.show();
		try {
			const moduleMenuMap = await getPageModuleMenuMap(targetPage);
			const targetMenuId = moduleMenuMap?.get(moduleId);

			if (!targetMenuId) {
				LoadingMask.hide();
				console.warn(`[HK02498] 未找到模块 ${moduleId} 在 ${language} 下可渲染的顶层菜单，将回退首页`);
				return false;
			}

			const path = buildTemplatePath(buildMenuPath(targetMenuId, moduleId));
			// buildTemplateQuery 会用当前 locale 覆盖 lang，这里必须显式回写目标语言
			const query = { ...buildTemplateQuery(), lang: language };
			const targetUrl = router.resolve({ path, query }).href;
			window.location.replace(targetUrl);
			return true;
		} catch (error) {
			LoadingMask.hide();
			console.warn('[HK02498] 切换语言并保持当前菜单失败', error);
			return false;
		}
	};

	return {
		handleLanguageSelect
	};
}
